import React, { Ref, useMemo } from 'react';
import { isEmpty } from 'ramda';
import { SendCommunicationFirstStepForm, SendCommunicationFormLabels } from 'types';
import { CheckOutlined } from '@ant-design/icons';
import { Row, Col, Space } from 'antd';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useTheme } from 'common/styling/theme';

import { useIntl } from 'common/config/i18n';
import {
  calculateClientsType,
  calculateExcludedStations,
  calculateImpactedStations,
  calculateOthers,
  calculateSelectedCarriages,
} from './utils';
import { OtherSelectionSelectors, SendCommunicationSelectors } from 'common/config/store';
import Card from 'components/molecules/card';

export interface Props {
  formValues: SendCommunicationFirstStepForm;
  elemRef?: Ref<HTMLDivElement>;
  isBulkCommunication?: boolean;
}

const StyledUl = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
  width: 100%;

  li {
    list-style-type: none;
    margin: 0;
    padding: 0;
    font-weight: bold;
    .icon {
      padding: 0 8px 0 0px;
      position: relative;
      top: 2px;
    }
  }

  p {
    margin: 0;
    padding: 0;
    display: inline;
    font-size: 14px;
  }
`;

const ReminderSelections: React.FC<Props> = ({ formValues, elemRef, isBulkCommunication }) => {
  const { t } = useIntl();
  const { theme } = useTheme();

  const stationsConcerned = useSelector(SendCommunicationSelectors.stationsConcernedSelector);
  const selectedServiceType = useSelector(SendCommunicationSelectors.selectedServiceTypeSelector);

  const typologies = useSelector(OtherSelectionSelectors.typologiesSelector);
  const conflictTypes = useSelector(OtherSelectionSelectors.conflictTypesSelector);

  const data: { [key in SendCommunicationFormLabels]: string } = useMemo(() => {
    return {
      clientsType: calculateClientsType(formValues.customersSelection, t),
      impactedStations: calculateImpactedStations(formValues.impactedStations, stationsConcerned),
      excludedStations: calculateExcludedStations(formValues.excludedStations, stationsConcerned),
      selectedCarriages: calculateSelectedCarriages(formValues.selectedCarriages, selectedServiceType, t),
      Others: calculateOthers({
        selectedTypologiesIds: formValues.selectedTypologiesIds,
        typologies: typologies,
        translate: t,
        conflictTypes: conflictTypes,
        selectedConflictTypeId: formValues.selectedConflictTypeId,
        excludeAllConflictTypes: formValues.excludeAllConflictTypes,
        selectedPurchaseComparison: formValues.selectedPurchaseComparison,
        selectedPurchaseDate: formValues.purchaseReferenceDate,
        selectedPurchaseTime: formValues.purchaseReferenceTime,
      }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, typologies, conflictTypes]);

  if (!data || isEmpty(data)) {
    return null;
  }

  const hasValueColor = theme.colors.primary.main;
  const hasNotValueColor = theme.colors.grey.dark;
  const keys = Object.keys(data) as SendCommunicationFormLabels[];

  // Rows/<li> should be displayed into columns, 3 by 3
  let Cols = [];
  if (isBulkCommunication) {
    // excludedStations block doesnt need to be shown
    Cols = [[keys[0], keys[1], keys[3]], [keys[4]]];
  } else {
    Cols = [
      [keys[0], keys[1], keys[2]],
      [keys[3], keys[4]],
    ];
  }

  return (
    <>
      {keys.length > 0 && (
        <Card
          elemRef={elemRef}
          wrapperStyle={{
            backgroundColor: theme.colors.primary.light,
            alignItems: 'flex-start',
            margin: 0,
            maxWidth: '100%',
          }}
          full
        >
          <StyledUl>
            <Row gutter={32}>
              {Cols.map((col, idx) => (
                <Col sm={idx === 0 ? 10 : idx === 1 ? 14 : undefined} key={idx}>
                  <Space direction="vertical">
                    {col.map((key) => {
                      const value = data?.[key] || '';
                      const label = t('sendCommunication', 'reminderSelections', key);
                      return (
                        <li key={key} style={isEmpty(value) ? { color: hasNotValueColor } : { color: hasValueColor }}>
                          {!!label && (
                            <>
                              <span className="icon">
                                <CheckOutlined
                                  style={{ fontSize: 18, color: isEmpty(value) ? hasNotValueColor : hasValueColor }}
                                />
                              </span>
                              <p>{`${label}: ${value}`}</p>
                            </>
                          )}
                        </li>
                      );
                    })}
                  </Space>
                </Col>
              ))}
            </Row>
          </StyledUl>
        </Card>
      )}
    </>
  );
};

export default ReminderSelections;
